import { styled } from '@linaria/react';
import { SegmentedControl } from '@mantine/core';
import { Icon } from '../../../ui-library/icon/icon';

/** Which layout the Flora Pages gallery renders in. */
type FloraPagesView = 'grid' | 'list';

interface FloraPagesViewToggleProps {
  value: FloraPagesView;
  onChange: (view: FloraPagesView) => void;
}

/**
 * Grid/list layout switch for the Flora Pages gallery, styled to match the
 * Documents view's segmented control (white indicator on a subtle track).
 *
 * @param {FloraPagesViewToggleProps} props - Current view and change handler.
 * @returns {JSX.Element} The two-option segmented control.
 */
export function FloraPagesViewToggle({ value, onChange }: FloraPagesViewToggleProps) {
  return (
    <SegmentedControl
      value={value}
      onChange={(next) => onChange(next as FloraPagesView)}
      size="xs"
      aria-label="Pages layout"
      data={[
        {
          value: 'grid',
          label: (
            <SegmentLabel>
              <span aria-hidden="true">
                <Icon name="grid_view" size={18} />
              </span>
              <VisuallyHidden>Grid view</VisuallyHidden>
            </SegmentLabel>
          ),
        },
        {
          value: 'list',
          label: (
            <SegmentLabel>
              <span aria-hidden="true">
                <Icon name="format_list_bulleted" size={18} />
              </span>
              <VisuallyHidden>List view</VisuallyHidden>
            </SegmentLabel>
          ),
        },
      ]}
      styles={{
        root: { background: 'var(--sky-lighter)', borderRadius: 9, padding: 3 },
        label: { color: 'var(--ink-light)', padding: '4px 8px' },
        indicator: { background: 'var(--base-white)', borderRadius: 7, boxShadow: 'var(--box-shadow-small-soft)' },
      }}
    />
  );
}

const SegmentLabel = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const VisuallyHidden = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

export type { FloraPagesView };
